<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" style="height: 100%;">
        <div class="left-right-panel-reveal-container block-container">
          <div
            class="block"
            v-for="(block, blockIndex) in blocks"
            :id="[`block-${blockIndex}`]"
            :track-by="blockIndex"
            :key="blockIndex"
          >
            <div
              class="block-paragraph"
              v-for="(step, stepIndex) in block"
              :track-by="stepIndex"
              :key="stepIndex"
            >
              <blinking-cursor-text
                :text="step.answer"
                :text-step="progress.stepIndex"
                :shouldBeCurrentStep="blockIndex === currentBlock && stepIndex === currentBlockStep"
                :shouldBeCompletedStep="blockIndex <= currentBlock && stepIndex < currentBlockStep"
              >
              </blinking-cursor-text>
            </div>
          </div>

          <div id="img-overlay">
            <div id="img-background">
              <img :src="image" />
            </div>
          </div>

          <div id="end-instructions">
            {{ endInstructions }}
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { groupBy } from 'lodash';
import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import AccessibilityHelper from 'mixins/AccessibilityHelper';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import { TweenMax, Back } from 'gsap';
import ActivityStage from '../components/activity/ActivityStage.vue';
import BlinkingCursorText from '../components/text-displays/BlinkingCursorText.vue';

export default {
  mixins: [AssetLoader, ActivityManager, TypingActivityHelper, ErrorLogger, AccessibilityHelper],

  components: {
    ActivityStage,
    BlinkingCursorText
  },

  data() {
    return {
      image: null,
      endInstructions: '',
      activityName: 'LeftRightPanelReveal',
      blocks: [],
      currentBlock: 0,
      currentBlockStep: 0
    };
  },
  methods: {
    async loadActivity() {
      this.setUpActivity();
      this.createRounds();
      await this.loadImages();
    },

    setUpActivity() {
      const [{ instructions, answerImage, steps }] = this.activityData.rounds;

      const blocks = Object.values(groupBy(steps, 'column'));

      this.endInstructions = instructions;
      this.blocks = blocks;
      this.image = answerImage?.image?.src;
    },

    async loadImages() {
      this.queueImage(this.image);
      return this.downloadImages();
    },

    startActivity() {
      this.setupNewRound();
      this.showKeyboard({ enable: true });
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);
      this.$nextTick(this.fadeInSlideItOver);
      this.$nextTick(() => {
        if (this.isMobile) {
          this.setMobileImgHeight();
        }
      });
    },

    showInstructions() {
      const elem = document.getElementById('end-instructions');
      elem.style.opacity = 1;
    },

    fadeInSlideItOver() {
      const elem = document.querySelector('.left-right-panel-reveal-container');
      TweenMax.to(elem, 1, { css: { opacity: 1 } });
    },

    fadeOutCompletedParagraphs() {
      const elem = document.getElementById(`block-${this.currentBlock}`);

      if (elem) {
        TweenMax.to(elem, 1, { css: { opacity: 0 } });
      }
    },

    revealPhoto() {
      const isLastBlock = this.currentBlock === this.blocks.length - 1;
      const block = document.getElementById(`block-${this.currentBlock}`);
      const posX = `${(this.currentBlock + 1) * (100 / this.blocks.length)}%`;
      const width = isLastBlock
        ? // set full width for last block
          document.querySelector('.activity-stage').scrollWidth
        : // set partial width for end of step
          posX;

      TweenMax.to('#img-overlay', 2, { width, ease: Back.easeInOut });

      if (isLastBlock) {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      this.showInstructions();
      TweenMax.delayedCall(1, this.hideKeyboard);
      TweenMax.delayedCall(1, this.playCongrats);
      TweenMax.delayedCall(4, () => this.completeActivity(false));
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      // we've reached end of paragrah
      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();
        this.currentBlockStep = this.currentBlockStep + 1;

        const shouldGoToNextBlock =
          this.currentRound?.steps?.[this.progress.stepIndex - 1]?.column !==
          this.currentStep?.column;

        if (shouldGoToNextBlock) {
          this.fadeOutCompletedParagraphs();
          this.revealPhoto();
          this.currentBlock = this.currentBlock + 1;
          this.currentBlockStep = 0;
        }
      }
    },

    setMobileImgHeight() {
      const activityStageEl = document.querySelector('.activity-stage');
      const imgBackgroundImgEl = document.querySelector('#img-background img');

      if (activityStageEl && imgBackgroundImgEl) {
        imgBackgroundImgEl.style.height = `${activityStageEl.clientHeight - 45}px`;
      }
    }
  },

  watch: {
    expectedKey() {
      const { stepIndex } = this.progress;
      const flattendedBlocks = this.blocks?.flat();

      const phrase = flattendedBlocks[stepIndex]?.answer;

      if (!phrase) return;

      const instructions = `Type the phrase - ${phrase}`;

      if (instructions === this.currentScreenReaderAlert) {
        return;
      }

      this.rerenderScreenReaderAlert(instructions);
    }
  }
};
</script>

<style lang="scss" scoped>
.left-right-panel-reveal-container {
  opacity: 0;
}

.block-container {
  align-content: stretch;
  align-items: stretch;
  display: flex;
  flex-direction: row;
  height: 100%;
  position: relative;
  min-height: 400px;
  width: 1024px;
}

.block {
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-end;
  margin-left: 20px;
  letter-spacing: 2px;
  font-size: 18px;
}

.block-paragraph {
  max-width: 200px;
  z-index: 103;
}

#end-instructions {
  margin-left: auto;
  margin-right: auto;
  margin-top: 30px;
  text-align: center;
  font-size: 30px;
  font-family: 'hwtfutura';
  opacity: 0;
}

#img-overlay {
  top: 0;
  left: 0;
  height: 100%;
  width: 0;
  overflow: hidden;
  position: absolute;
  border-right: 4px solid #000;
  z-index: 100;
}

#img-background {
  width: 1024px;
  height: 316px;
  background-repeat: no-repeat;
  background-position: left top;

  img {
    width: 1024px;
    height: 316px;
  }
}

@media (min-width: 1500px) {
  #img-background {
    height: 316px;
  }
}

.chrome-os {
  .block-container {
    min-height: auto;
  }
}

.tablet {
  .block-container {
    min-height: auto;
    width: 100%;

    #img-background {
      img {
        height: auto;
        width: 100vw;
      }
    }
  }
}
</style>
